import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';

import {
	STATUS,
	STOCK_DELIVERY_ENDS,
} from '../../../common/constants';
import { getStockDeliveryDisplayStatus } from '../../../common/utils/warehousing/stockDeliveryStatus';

import Text from '../../components/text/Text';
import { ListItem } from '../../components/list/ListItem';
import { StatusBadge } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const getDeliveryEndLabel = (endType, endObj) => {
	const endName = endObj?.name || 'Unknown';

	if (endType === STOCK_DELIVERY_ENDS.WAREHOUSE) {
		return `Warehouse: ${endName}`;
	}

	if (endType === STOCK_DELIVERY_ENDS.SUPPLIER) {
		return `Supplier: ${endName}`;
	}

	return endName;
};

const StockDeliveryListItem = ({ item, customProps }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { onPressItem, canViewDetail, canViewPurchaseOrderDetail } = customProps || {};

	const handlePress = useCallback(() => {
		if (canViewDetail && onPressItem) {
			onPressItem(item);
		}
	}, [canViewDetail, item, onPressItem]);

	const handleOpenPurchaseOrder = useCallback(() => {
		if (canViewPurchaseOrderDetail && item?.purchaseOrder?.id) {
			navigation.navigate('PurchaseOrderDetailView', { id: item.purchaseOrder.id });
		}
	}, [canViewPurchaseOrderDetail, item?.purchaseOrder?.id, navigation]);

	const createdAt = useMemo(() => {
		return item.created ? moment(item.created).format('DD MMM YYYY') : 'Unknown';
	}, [item.created]);

	const displayStatus = getStockDeliveryDisplayStatus(item);
	const sourceLabel = getDeliveryEndLabel(item.source, item.sourceObj);
	const destinationLabel = getDeliveryEndLabel(item.destination, item.destinationObj);

	return (
		<ListItem
			onPress={canViewDetail ? handlePress : undefined}
			style={styles.item}
		>
			<View style={styles.header}>
				<Text style={styles.title}>
					Stock delivery {item.identifier || '—'}
				</Text>
				<StatusBadge
					label={displayStatus.text}
					tone={displayStatus.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					size="small"
				/>
			</View>

			<Text style={styles.meta}>
				Created: {createdAt}
			</Text>
			<Text style={styles.meta}>
				From: {sourceLabel}
			</Text>
			<Text style={styles.meta}>
				To: {destinationLabel}
			</Text>
			{item.purchaseOrder?.identifier && (
				<Text style={[styles.meta, styles.purchaseOrder]}>
					Purchase order:{' '}
					<Text
						style={canViewPurchaseOrderDetail ? styles.purchaseOrderLink : undefined}
						onPress={canViewPurchaseOrderDetail ? handleOpenPurchaseOrder : undefined}
					>
						{item.purchaseOrder.identifier}
					</Text>
				</Text>
			)}
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xxs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xs,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	meta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	purchaseOrder: {
		marginTop: space.xs,
	},
	purchaseOrderLink: {
		...type.secondaryStrong,
		color: colors.accent,
		textDecorationLine: 'underline',
	},
});

export default StockDeliveryListItem;
